<template>
  <v-dialog
    persistent
    no-click-animation
    v-model="dialog"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    width="100%"
    max-width="800px"
  >
    <v-card>
      <v-card-title class="text-body-1 font-weight-semibold"
        >Task Overview</v-card-title
      >
      <v-card-subtitle
        >Below are the different items that require your attention. Click on any
        one to navigate to the item area.</v-card-subtitle
      >
      <v-divider />
      <v-card-text class="pa-0">
        <v-expansion-panels
          v-model="openPanels"
          multiple
          flat
          class="compact-panels"
        >
          <v-expansion-panel
            v-for="moduleGroup in normalizedActionItems"
            :key="`module-${moduleGroup.module}`"
          >
            <v-expansion-panel-header>
              <strong class="text-capitalize"
                >{{ moduleGroup.module }} ({{
                  moduleGroup.notifications.length
                }})</strong
              >
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-list
                dense
                style="max-height: 320px; overflow-y: auto"
                class="py-0"
              >
                <template
                  v-for="(items, category) in groupByCategory(
                    moduleGroup.notifications
                  )"
                >
                  <!-- <v-subheader :key="`subheader-${moduleGroup.module}-${category}`">
                    {{ categoryLabel(category) }} ({{ items.length }})
                  </v-subheader> -->

                  <v-list-item
                    v-for="item in items"
                    :key="`item-${item.hash}-${item.title}`"
                    @click="navigate(item)"
                    style="min-height: 36px"
                    class="px-5"
                  >
                    <v-list-item-action class="my-0 mr-1">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            v-on="on"
                            v-bind="attrs"
                            icon
                            x-small
                            @click.stop.prevent="
                              removeItem(moduleGroup.module, item)
                            "
                          >
                            <v-icon small>mdi-minus</v-icon>
                          </v-btn>
                        </template>
                        <span>Remind me tomorrow</span>
                      </v-tooltip>
                    </v-list-item-action>

                    <v-list-item-content class="py-1">
                      <v-list-item-title class="text-body-2 text-truncate">
                        <span
                          v-if="item.title && item.message"
                          class="font-weight-medium"
                          >{{ item.title }}</span
                        >
                        <span v-if="item.title && item.message"> — </span>
                        {{ item.message || item.title }}
                      </v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action class="my-0 ml-1">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            v-on="on"
                            v-bind="attrs"
                            icon
                            x-small
                            @click.stop.prevent="navigate(item)"
                          >
                            <v-icon small>mdi-chevron-right</v-icon>
                          </v-btn>
                        </template>
                        <span>Take me there</span>
                      </v-tooltip>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-divider />
      <v-card-actions class="d-flex justify-end">
        <v-btn text outlined class="text-capitalize" @click="close"
          >Close</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { ActionItemsService } from '@/modules/shared/services/action-items.service';
import { mapGetters } from 'vuex';

/**
 * Maps the backend's messageType strings to the section names used on the
 * Notification Management settings page. INFERRED by lining up messageType
 * values against the approved mockup screenshot's category names — not
 * confirmed with the API owner. Unmapped values fall back to their raw
 * string via categoryLabel() below.
 */
const MESSAGE_TYPE_LABELS = {
  'Delinquent Action Reminder': 'Delinquency Control Actions',
  'Delinquency Case Closure': 'Case Closure',
  'Delinquency Case Assignment': 'New Case Assignment',
  'Delinquency Case Escalation': 'Escalation',
  'Delinquency Action Assignment': 'Task Assignment',
  'Delinquency Performance Alert': 'Performance & Delinquency Alerts',
  'Company Level Alerts': 'Company-Level Alerts'
};

export default {
  mixins: [PageMixin],

  props: {
    /**
     * Accepts either the array shape ({ module, notifications }[]) or the
     * object-keyed-by-module shape the API currently returns. Normalized
     * below regardless of which one shows up.
     */
    actionItems: {
      type: [Array, Object],
      default: () => []
    }
  },

  data() {
    return {
      dialog: false,
      openPanels: [0],
      loading: true,
      localActionItems: null // null = use the prop as fallback until first fetch completes
    };
  },

  computed: {
    ...mapGetters('authModule', ['moduleAccess']),
    normalizedActionItems() {
      const source =
        this.localActionItems !== null
          ? this.localActionItems
          : this.actionItems;

      if (Array.isArray(source)) {
        return source;
      }

      return Object.entries(source || {})
        .filter(([module]) => this.moduleAccess(module))
        .map(([module, notifications]) => ({
          module,
          notifications: notifications || []
        }));
    }
  },

  methods: {
    async open() {
      this.dialog = true;
      await this.refreshActionItems();
    },

    async refreshActionItems() {
      this.loading = true;
      try {
        const svc = new ActionItemsService();
        const response = await svc.getActionItemsForLoggedInUser();
        this.localActionItems = response.data;
      } catch (error) {
        console.error('Failed to refresh action items:', error);
        // Keep whatever was already showing rather than blanking the dialog on a failed refresh
      } finally {
        this.loading = false;
      }
    },

    close() {
      // TODO use cookie or local storage value to remember if the user has seen the action items
      // Maybe use a cookie with a 1 day expiry
      this.dialog = false;
    },

    navigate(item) {
      this.$router.push(item.link);
      this.close();
    },

    groupByCategory(notifications) {
      return notifications.reduce((groups, item) => {
        const key = item.messageType || 'Other';
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
        return groups;
      }, {});
    },

    categoryLabel(messageType) {
      return MESSAGE_TYPE_LABELS[messageType] || messageType;
    },

    removeItem(moduleName, item) {
      const s = new ActionItemsService();
      s.addToIgnoreList(item);

      this.$emit('ignore', { key: moduleName, item });
    }
  }
};
</script>

<style scoped>
.compact-panels ::v-deep .v-expansion-panel-header {
  min-height: 40px;
  padding: 8px 24px;
}
</style>
